import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, functions } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { STokenLogo } from '../components/StreamDisplay';
import { useAdManager } from '../hooks/useAdManager';
import './DailyBonus.css';

const DAILY_REWARDS = {
  1:  { streams: 100,  label: 'Day 1' },
  2:  { streams: 150,  label: 'Day 2' },
  3:  { streams: 200,  label: 'Day 3' },
  4:  { streams: 250,  label: 'Day 4' },
  5:  { streams: 300,  label: 'Day 5' },
  6:  { streams: 350,  label: 'Day 6' },
  7:  { streams: 500,  label: 'Day 7', special: true },
  8:  { streams: 400,  label: 'Day 8' },
  9:  { streams: 450,  scratch: 1, label: 'Day 9' },
  10: { streams: 500,  label: 'Day 10' },
  11: { streams: 550,  label: 'Day 11' },
  12: { streams: 600,  label: 'Day 12' },
  13: { streams: 650,  label: 'Day 13' },
  14: { streams: 800,  scratch: 1, label: 'Day 14', special: true },
  15: { streams: 700,  label: 'Day 15' },
  16: { streams: 750,  label: 'Day 16' },
  17: { streams: 800,  label: 'Day 17' },
  18: { streams: 850,  label: 'Day 18', bonus: '+2% XP' },
  19: { streams: 900,  label: 'Day 19' },
  20: { streams: 1000, label: 'Day 20', special: true },
  21: { streams: 1100, scratch: 1, label: 'Day 21' },
  22: { streams: 950,  label: 'Day 22' },
  23: { streams: 1000, label: 'Day 23' },
  24: { streams: 1050, label: 'Day 24' },
  25: { streams: 1200, label: 'Day 25', special: true },
  26: { streams: 1100, scratch: 1, label: 'Day 26' },
  27: { streams: 1150, label: 'Day 27' },
  28: { streams: 1250, label: 'Day 28' },
  29: { streams: 1300, label: 'Day 29' },
  30: { streams: 1500, scratch: 2, label: 'Day 30', special: true },
  31: { streams: 2000, scratch: 1, label: 'Day 31', jackpot: true },
};

export default function DailyBonus() {
  const [currentDay, setCurrentDay] = useState(1);
  const [claimed, setClaimed]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [justClaimed, setJustClaimed] = useState(null);
  const navigate = useNavigate();
  const userRef = useRef(null);
  const { showSilentAd } = useAdManager();

  useEffect(() => {
    // auth.currentUser is guaranteed by AuthGuard — no need to wait for onAuthStateChanged
    const user = auth.currentUser;
    userRef.current = user;
    if (!user) { setLoading(false); return; }

    const timeout = setTimeout(() => setLoading(false), 3000);

    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const data = snap.exists() ? snap.data() : {};

        const lastClaim = data?.lastDailyBonusDate
          ? (data.lastDailyBonusDate.toDate ? data.lastDailyBonusDate.toDate() : new Date(data.lastDailyBonusDate))
          : null;
        const today = new Date();
        const isSameDay = lastClaim &&
          lastClaim.getDate() === today.getDate() &&
          lastClaim.getMonth() === today.getMonth() &&
          lastClaim.getFullYear() === today.getFullYear();

        setClaimed(isSameDay);
        setCurrentDay(data?.dailyBonusDay || 1);
      } catch (e) {
        console.error('fetchUser:', e);
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    };
    fetchData();
    return () => clearTimeout(timeout);
  }, []);

  const claimReward = async () => {
    if (claimed || !userRef.current) return;
    try {
      const { data } = await httpsCallable(functions, 'claimDailyBonus')();
      setJustClaimed({ streams: data.streams, xp: data.xp });
      setClaimed(true);
      setCurrentDay(data.nextDay);
      showSilentAd();
    } catch (e) {
      if (e?.message?.includes('ALREADY_CLAIMED')) {
        setClaimed(true);
      } else {
        console.error('claimReward:', e);
      }
    }
  };

  if (loading) return (
    <div className="page daily-bonus">
      <div className="skeleton" style={{ height: 180, borderRadius: 16 }} />
    </div>
  );

  const reward = DAILY_REWARDS[currentDay];

  return (
    <div className="page daily-bonus">
      <div className="grid-bg" aria-hidden="true" />

      <header className="daily-bonus__header">
        <button className="page-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Home
        </button>
        <h1 className="daily-bonus__title">Daily Bonus</h1>
        <p className="daily-bonus__subtitle">Claim every day — don't break the streak.</p>
      </header>

      {/* Claimed toast */}
      {justClaimed && (
        <div className="daily-bonus__toast">
          <STokenLogo size={20} />
          <span>+{justClaimed.streams} Streams · +{justClaimed.xp} XP claimed!</span>
        </div>
      )}

      {/* Today's reward card */}
      <div className={`card${reward.jackpot ? ' card--brand' : ''} daily-bonus__today`}>
        <div className="daily-bonus__today-top">
          <div className="daily-bonus__day-badge">
            <span className="daily-bonus__day-num">{currentDay}</span>
            <span className="daily-bonus__day-of">/ 31</span>
          </div>
          {reward.jackpot && <span className="badge badge--brand">JACKPOT</span>}
          {reward.special && !reward.jackpot && <span className="badge badge--accent">BONUS DAY</span>}
        </div>

        <div className="daily-bonus__reward-row">
          <STokenLogo size={28} />
          <span className="daily-bonus__streams">+{reward.streams}</span>
          <span className="daily-bonus__streams-label">Streams</span>
        </div>

        {reward.scratch && (
          <div className="daily-bonus__extra">
            🎟️ +{reward.scratch} Scratch Card{reward.scratch > 1 ? 's' : ''}
          </div>
        )}
        {reward.bonus && (
          <div className="daily-bonus__extra">⚡ {reward.bonus}</div>
        )}

        <button
          className={`btn btn--full ${claimed ? 'btn--ghost' : 'btn--primary'}`}
          style={{ marginTop: 'var(--sp-4)' }}
          onClick={claimReward}
          disabled={claimed}
        >
          {claimed ? '✓ Claimed Today' : 'Claim Reward'}
        </button>
      </div>

      {/* 31-day calendar */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Reward Calendar</div>
      <div className="daily-bonus__calendar">
        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => {
          const r = DAILY_REWARDS[day];
          const isCurrent  = day === currentDay;
          const isComplete = day < currentDay;

          return (
            <div
              key={day}
              className={`daily-bonus__day${isCurrent ? ' daily-bonus__day--current' : ''}${isComplete ? ' daily-bonus__day--done' : ''}${r.jackpot ? ' daily-bonus__day--jackpot' : ''}`}
            >
              <span className="daily-bonus__day-label">{day}</span>
              <span className="daily-bonus__day-streams">{r.streams}</span>
              {r.scratch && <span className="daily-bonus__day-ticket">🎟️</span>}
              {isComplete && <span className="daily-bonus__day-check">✓</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
